/**
 * "What you missed" — the end-of-session review list.
 *
 * Renders one row per wrong answer, using the app's own `MissRow` when it
 * registers one and the shared term → translation row otherwise. Renders
 * nothing on a clean session: the score already says "Perfect!", and an empty
 * "What you missed (0)" heading would be a consolation prize nobody asked for.
 *
 * The list is built entirely from what the practice screen already had in
 * memory. It is not fetched, so it does not survive a refresh — the same is
 * already true of the score it sits under.
 */
import { getAppDefinition } from '@/apps';
import { SharedMissRow } from '@/components/practice/SharedMissRow';
import type { Card, SessionMiss } from '@/core/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyCard = Card<any>;

export function MissedList({ appId, misses }: { appId: string; misses: SessionMiss[] }) {
  if (misses.length === 0) return null;

  const RowComponent = getAppDefinition(appId)?.MissRow ?? SharedMissRow;

  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-muted-foreground flex items-baseline justify-between text-xs font-medium tracking-wider uppercase">
        <span>What you missed</span>
        <span className="tabular-nums">{misses.length}</span>
      </h2>
      <ul className="divide-border divide-y">
        {misses.map((miss) => (
          <RowComponent
            key={miss.card.id}
            card={miss.card as AnyCard}
            user_answer={miss.user_answer}
          />
        ))}
      </ul>
    </section>
  );
}
